import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { useDependencies } from '@/scopes/common/infrastructure/dependencies';
import { createClickedFlightEvent } from '@/domain/prices/analytics/index';
import { TransportType } from '@/infrastructure/prices/transport/api/urlBuilder/transportSelectorUrlBuilder';
import { usePricesStore } from '@/infrastructure/prices/usePricesStore';
import { useAccommodationContext } from '@/react-pages/Prices/AccommodationContext';
import { useFiltersQuery } from '../filters/hooks';
import { useRoomSelector } from '../rooms/hooks';
import type { TransportSelectorMappedResponse } from './types';
import { FlightType } from '@/domain/prices/transports/types';
import { useAccommodationAvailability } from '@/domain/prices/availability/hooks';

const mockedBusResponse: TransportSelectorMappedResponse = {
  transportType: 'Bus',
  outboundRoutes: [
    {
      id: '1',
      airline: null,
      arrivalDate: {
        time: '12:30',
        fullDate: 'zondag 19 januari 2025',
      },
      departureDate: {
        time: '10:30',
        fullDate: 'zondag 19 januari 2025',
      },
      flightType: null,
      luggage: null,
      price: 100,
      route: {
        from: { code: 'MAD', name: 'Madrid' },
        to: { code: 'BCN', name: 'Barcelona' },
      },
      stationName: 'Estación Sur',
    },
    {
      id: '2',
      airline: null,
      arrivalDate: {
        time: '11:30',
        fullDate: 'zondag 19 januari 2025',
      },
      departureDate: {
        time: '10:30',
        fullDate: 'zondag 19 januari 2025',
      },
      flightType: null,
      luggage: null,
      price: 100,
      route: {
        from: { code: 'MAD', name: 'Madrid' },
        to: { code: 'BCN', name: 'Barcelona' },
      },
      stationName: 'Estación Sur',
    },
    {
      id: '3',
      airline: null,
      arrivalDate: {
        time: '13:30',
        fullDate: 'zondag 19 januari 2025',
      },
      departureDate: {
        time: '10:30',
        fullDate: 'zondag 19 januari 2025',
      },
      flightType: null,
      luggage: null,
      price: 100,
      route: {
        from: { code: 'MAD', name: 'Madrid' },
        to: { code: 'BCN', name: 'Barcelona' },
      },
      stationName: 'Estación Sur',
    },
  ],
  inboundRoutes: [
    {
      id: '1',
      airline: null,
      arrivalDate: {
        time: '14:30',
        fullDate: 'zondag 19 januari 2025',
      },
      departureDate: {
        time: '10:30',
        fullDate: 'zondag 19 januari 2025',
      },
      flightType: 'flexible',
      luggage: null,
      price: 100,
      route: {
        from: { code: 'MAD', name: 'Madrid' },
        to: { code: 'BCN', name: 'Barcelona' },
      },
      stationName: 'Estacio de Franca',
    },
    {
      id: '2',
      airline: null,
      arrivalDate: {
        time: '15:30',
        fullDate: 'zondag 19 januari 2025',
      },
      departureDate: {
        time: '10:30',
        fullDate: 'zondag 19 januari 2025',
      },
      flightType: 'flexible',
      luggage: null,
      price: 100,
      route: {
        from: { code: 'MAD', name: 'Madrid' },
        to: { code: 'BCN', name: 'Barcelona' },
      },
      stationName: 'Estacio de Franca',
    },
    {
      id: '3',
      airline: null,
      arrivalDate: {
        time: '16:30',
        fullDate: 'zondag 19 januari 2025',
      },
      departureDate: {
        time: '10:30',
        fullDate: 'zondag 19 januari 2025',
      },
      flightType: 'flexible',
      luggage: null,
      price: 100,
      route: {
        from: { code: 'MAD', name: 'Madrid' },
        to: { code: 'BCN', name: 'Barcelona' },
      },
      stationName: 'Estacio de Franca',
    },
  ],
  tripSummary: [],
};

export function useFlightSelectorQuery() {
  const { flightSelectorApiClient } = useDependencies();
  const { contextItemId, accoId, bookingGateId } = useAccommodationContext();
  const { filterSelection } = useFiltersQuery();
  const { allRoomsSelected, selectedRoomIds, selectedRoomContractIds } = useRoomSelector();
  const { selectedPriceId } = usePricesStore();
  const { isFlightOnly } = useAccommodationAvailability();

  const filtersCriteria = {
    contextItemId,
    bookingGateId,
    accoId,
    filterSelection,
    selectedPriceId: selectedPriceId || '',
    roomIds: isFlightOnly ? ['1XXX'] : selectedRoomIds,
    roomContractIds: selectedRoomContractIds,
  };

  const isEnabled =
    (Boolean(allRoomsSelected) && filterSelection.TransportType === TransportType.Flight) ||
    (Boolean(selectedPriceId) && isFlightOnly);

  return useQuery({
    queryKey: ['flightSelector', JSON.stringify(filtersCriteria)],
    queryFn: () => flightSelectorApiClient.getFlights(filtersCriteria),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isEnabled,
  });
}

export function useSelfDriveSelectorQuery() {
  const { selfDriveSelectorApiClient } = useDependencies();
  const { contextItemId, accoId, bookingGateId } = useAccommodationContext();
  const { filterSelection } = useFiltersQuery();
  const { allRoomsSelected, selectedRoomIds, selectedRoomContractIds } = useRoomSelector();
  const { selectedPriceId } = usePricesStore();

  const filtersCriteria = {
    contextItemId,
    bookingGateId,
    accoId,
    filterSelection,
    selectedPriceId: selectedPriceId || '',
    roomIds: selectedRoomIds,
    roomContractIds: selectedRoomContractIds,
  };

  return useQuery({
    queryKey: ['selfDriveSelector', JSON.stringify(filtersCriteria)],
    queryFn: () => selfDriveSelectorApiClient.getSelfDrive(filtersCriteria),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(allRoomsSelected) && filterSelection.TransportType === TransportType.SelfDrive,
  });
}

function useBusQuery() {
  const { isLoading, isFetching, data, isError } = useQuery({
    queryKey: ['bus'],
    queryFn: async () => mockedBusResponse,
  });

  return {
    isLoading,
    isFetching,
    data,
    isError,
  };
}

export function useFlightSelector() {
  const { sendEvent } = useAnalytics();
  const { isLoading, isFetching, data, isError } = useFlightSelectorQuery();
  const { selectedInboundRouteId, selectedOutboundRouteId, setInboundRouteId, setOutboundRouteId } = usePricesStore();

  const relatedInboundRoutes = React.useMemo(
    () => data?.outboundRoutes.find((route) => route.id === selectedOutboundRouteId)?.relatedRoutes || [],
    [data?.outboundRoutes, selectedOutboundRouteId]
  );
  const inboundRoutes = relatedInboundRoutes || [];
  const outboundRoutes = data?.outboundRoutes || [];
  const outboundRoute = outboundRoutes.find((route) => route.id === selectedOutboundRouteId) || null;
  const inboundRoute = inboundRoutes.find((route) => route.id === selectedInboundRouteId) || null;

  function setOutBoundRouteProxy(id: string) {
    setOutboundRouteId(id);
    sendEvent(createClickedFlightEvent(FlightType.Outbound));
    setInboundRouteId(null);
  }

  function setInBoundRouteProxy(id: string) {
    setInboundRouteId(id);
    sendEvent(createClickedFlightEvent(FlightType.Inbound));
  }

  React.useEffect(() => {
    if (!selectedOutboundRouteId) {
      setOutboundRouteId(data?.outboundRoutes[0]?.id || null);
    } else if (!data?.outboundRoutes) {
      setOutboundRouteId(null);
    }
  }, [selectedOutboundRouteId, setOutboundRouteId, data?.outboundRoutes]);

  React.useEffect(() => {
    if (!selectedInboundRouteId || !relatedInboundRoutes.some((route) => route.id === selectedInboundRouteId)) {
      setInboundRouteId(relatedInboundRoutes[0]?.id || null);
    }
  }, [selectedInboundRouteId, setInboundRouteId, relatedInboundRoutes]);

  return {
    isLoading,
    isFetching,
    isError,
    inboundRoutes,
    outboundRoutes,
    inboundRoute,
    outboundRoute,
    selectedInboundRouteId,
    selectedOutboundRouteId,
    setInboundRouteId: setInBoundRouteProxy,
    setOutboundRouteId: setOutBoundRouteProxy,
  };
}

export function useBusSelector() {
  const { data } = useBusQuery();
  const { selectedInboundRouteId, selectedOutboundRouteId, setInboundRouteId, setOutboundRouteId } = usePricesStore();

  React.useEffect(() => {
    if (!selectedInboundRouteId) {
      setInboundRouteId(data?.inboundRoutes[0]?.id || null);
    }
    if (!selectedOutboundRouteId) {
      setOutboundRouteId(data?.outboundRoutes[0]?.id || null);
    }
  }, [
    selectedInboundRouteId,
    selectedOutboundRouteId,
    setInboundRouteId,
    setOutboundRouteId,
    data?.inboundRoutes,
    data?.outboundRoutes,
  ]);

  const inboundRoutes = data?.inboundRoutes || [];
  const outboundRoutes = data?.outboundRoutes || [];
  const outboundRoute = outboundRoutes.find((route) => route.id === selectedOutboundRouteId) || null;
  const inboundRoute = inboundRoutes.find((route) => route.id === selectedInboundRouteId) || null;

  return {
    inboundRoutes,
    outboundRoutes,
    selectedInboundRouteId,
    selectedOutboundRouteId,
    inboundRoute,
    outboundRoute,
    setInboundRouteId,
    setOutboundRouteId,
  };
}

export function useSelfDriveSelector() {
  const { isLoading, isFetching, data } = useSelfDriveSelectorQuery();

  return {
    isLoading,
    isFetching,
    tripSummary: data?.tripSummary || [],
    priceBreakdown: data?.priceBreakdown || {},
    contentUsps: data?.contentUsps || [],
  };
}

export function useTransportSelector() {
  const { isFetching: selfDriveFetching, data: selfDriveData } = useSelfDriveSelectorQuery();
  const { isFetching: flightFetching, data: flightData } = useFlightSelectorQuery();
  const { inboundRoute } = useFlightSelector();

  const tripSummary = flightData?.tripSummary || [];
  const flightSummary = [...tripSummary, ...(inboundRoute?.tripSummary || [])];

  return {
    isFetching: selfDriveFetching || flightFetching,
    tripSummary: selfDriveData?.tripSummary || flightSummary || [],
    priceBreakdown: selfDriveData?.priceBreakdown || inboundRoute?.priceBreakdown,
  };
}
